import { useAppStore } from '@/stores/app';
import type { Org } from '@/types';
import RadioGroup from '@nautilusbio/nb-design-system/RadioGroup';
import React, { type FC, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

const SelectionCellRenderer: FC<{ data: Org }> = ({ data }) => {
  const { t } = useTranslation('orgs');

  const { selectedOrganization, setSelectedOrganization } = useAppStore(
    useShallow(state => ({
      selectedOrganization: state.selectedOrganization,
      setSelectedOrganization: state.setSelectedOrganization,
    })),
  );

  const isSelected = selectedOrganization?.id === data.id;

  const handleChange = useCallback(() => {
    setSelectedOrganization(data);

    // Persist per-user org selection
    const userString = localStorage.getItem('auth_user');
    let userId = null;
    if (userString) {
      try {
        const user = JSON.parse(userString);
        userId = user.id;
      } catch (err) {
        console.error('Failed to parse auth_user from localStorage:', err, userString);
      }
    }
    if (data && userId) {
      try {
        localStorage.setItem(`selectedOrgId_${userId}`, String(data.id));
        toast.success(t('org_switched_success', { name: data.name }));
      } catch {
        toast.error(t('org_switched_save_error', { name: data.name }));
      }
    } else {
      toast.success(t('org_switched_success', { name: data.name }));
    }
  }, [data, setSelectedOrganization, t]);

  return (
    <RadioGroup
      aria-label={t('select_organization', { name: data.name })}
      data-testid={`org-select-${data.id}`}
      id={`org-select-${data.id}`}
      label=""
      onChange={handleChange}
      options={[{ children: '', value: data.id }]}
      size="sm"
      value={isSelected ? data.id : ''}
    />
  );
};

export default memo(SelectionCellRenderer);
